import React, { useState } from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  InputLabel,
  ListItemText,
  ListItemIcon,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { Stop } from '@mui/icons-material';
import ErrorIcon from '@mui/icons-material/Error';

import ErrorMessage from '../../common/error-message';
import RouteBuilderMap from './route-builder-map';
import DragListContainer from './drag-list-container';
import { ZoneHexColors } from '../../../lib/utils';

const styles = {
  flexRow: {
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
  },
  icon: {
    marginLeft: '8px',
    marginTop: '3px',
  },
  textField: {
    width: '30%',
  },
  button: {
    width: '140px',
    fontSize: '12px',
  },
  error: {
    width: '300px',
    marginTop: '20px',
    float: 'right',
    marginRight: '20px',
  },
  child: {
    display: 'flex',
  },
  draggable: {
    marginLeft: '16px',
    marginTop: '82px',
  },
};

function BaseRouteBuilderModal({
  open, allLocations, handleSave, handleClose, route,
  geofences, pickupZones, scooters, getLocationsForRoute,
}) {
  const [currentRoute, setCurrentRoute] = useState(route);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // use for shared route being built between children
  const [builtRoute, setBuiltRoute] = useState(getLocationsForRoute(route));
  const [changedLocations, setChangedLocations] = useState([]);

  const onSave = () => {
    if (!currentRoute.name) {
      setError(true);
      setErrorMessage('Please enter a name.');
      return;
    }

    if (!currentRoute.hexColor) {
      setError(true);
      setErrorMessage('Please select a color.');
      return;
    }

    handleSave(currentRoute, changedLocations);
  };

  const onSaveLocations = () => {
    const locationIds = builtRoute.map((location) => location._id);
    if (locationIds.length === 0) {
      setError(true);
      setErrorMessage('Please select a location.');
      return;
    }

    currentRoute.locations = locationIds;
    setCurrentRoute(currentRoute);

    onSave();
  };

  const handleChangeRoute = (event) => {
    const newRoute = {
      ...currentRoute,
      [event.target.name]: event.target.value,
    };

    setCurrentRoute(newRoute);
  };

  return (
    <Dialog
      maxWidth="lg"
      open={open}
      onClose={handleClose}
    >
      <DialogContent>
        <Grid style={styles.flexRow}>
          <Typography variant="h5">Route Builder</Typography>
          <Tooltip title={'Click any hub in the map to add it to this route. The hub\'s sequence will be based on the order it was added to a route. You can reorder the hubs by dragging and dropping the hub names on the list.'}>
            <ErrorIcon style={styles.icon} />
          </Tooltip>
        </Grid>
        <Grid container item alignItems="center" sx={{ mt: 1 }}>
          <InputLabel sx={{ mt: 1, mr: 1 }}>
            Name
          </InputLabel>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            type="text"
            variant="standard"
            value={currentRoute.name || ''}
            style={styles.textField}
            onChange={handleChangeRoute}
          />
          <InputLabel sx={{ mt: 1, mr: 1, ml: 2 }}>
            Color
          </InputLabel>
          <Select
            onChange={handleChangeRoute}
            value={currentRoute.hexColor || ''}
            name="hexColor"
            variant="standard"
            style={styles.textField}
          >
            {Object.keys(ZoneHexColors).map((key) => (
              <MenuItem
                dense
                key={ZoneHexColors[key]}
                value={ZoneHexColors[key]}
              >
                <Grid container item>
                  <ListItemIcon>
                    <Stop style={{ color: ZoneHexColors[key] }} />
                  </ListItemIcon>
                  <ListItemText style={{ fontWeight: 'bold' }} primary={key} />
                </Grid>
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid style={styles.flexRow}>
          <div style={styles.child}>
            <RouteBuilderMap
              pickupZones={pickupZones}
              vehicleZones={geofences}
              vehicles={scooters}
              locations={allLocations}
              currentRouteLocations={builtRoute}
              currentRoute={currentRoute}
              setCurrentRouteLocations={setBuiltRoute}
              changedLocations={changedLocations}
              setChangedLocations={setChangedLocations}
            />
          </div>

          <div style={styles.draggable}>
            <DndProvider backend={HTML5Backend} context={window}>
              <DragListContainer
                locations={builtRoute}
                setLocations={setBuiltRoute}
                changedLocations={changedLocations}
                setChangedLocations={setChangedLocations}
              />
            </DndProvider>
          </div>
        </Grid>

        {error
          && (
          <div style={styles.error}>
            <ErrorMessage
              errorMessage={errorMessage}
            />
          </div>
          )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          color="primary"
          variant="outlined"
          style={styles.button}
        >
          Cancel
        </Button>
        <Button
          onClick={onSaveLocations}
          color="primary"
          variant="contained"
          style={styles.button}
        >
          Save and Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

BaseRouteBuilderModal.defaultProps = {};
BaseRouteBuilderModal.propTypes = {
  open: PropTypes.bool.isRequired,
  allLocations: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
    }).isRequired,
  ).isRequired,
  handleSave: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  getLocationsForRoute: PropTypes.func.isRequired,
  route: PropTypes.shape({
    _id: PropTypes.string,
  }).isRequired,
  geofences: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
    }).isRequired,
  ).isRequired,
  pickupZones: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
    }).isRequired,
  ).isRequired,
  scooters: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
    }).isRequired,
  ).isRequired,
};

const RouteBuilderModal = withRouter(BaseRouteBuilderModal);
export { BaseRouteBuilderModal, RouteBuilderModal };
